import { DollarSign, Languages, Check, Globe2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";

/** Currency dropdown — driven by the FX feed list + user profile currency. */
export function CurrencySwitcher({ testid, currency, fxList, t, isEs, onSelect }) {
  const list = fxList.length ? fxList : [currency];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button /* design-system: ALLOW legacy-todo */
          data-testid={testid}
          className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[11px] font-bold uppercase tracking-wider text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 shrink-0"
        >
          <DollarSign className="h-3.5 w-3.5" />
          {currency}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40 max-h-80 overflow-auto">
        <DropdownMenuLabel className="text-xs">{isEs ? "Moneda" : "Currency"}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {list.map((c) => (
          <DropdownMenuItem
            key={c}
            data-testid={`currency-option-${c}`}
            onClick={() => onSelect(c)}
            className="cursor-pointer"
          >
            <span className="flex-1 font-mono text-xs">{c}</span>
            {currency === c && <Check className="h-4 w-4 text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** EN / ES / PT language switcher. */
export function LangSwitcher({ testid, lang, setLang, t }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button /* design-system: ALLOW legacy-todo */
          data-testid={testid}
          className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[11px] font-bold uppercase tracking-wider text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 shrink-0"
        >
          <Languages className="h-3.5 w-3.5" />
          {lang.toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel className="text-xs">{t("language")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {[["en", "English"], ["es", "Español"], ["pt", "Português"]].map(([code, label]) => (
          <DropdownMenuItem
            key={code}
            data-testid={`lang-option-${code}`}
            onClick={() => setLang(code)}
            className="cursor-pointer"
          >
            <span className="flex-1">{label}</span>
            {lang === code && <Check className="h-4 w-4 text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


/** Country dropdown — drives billing region. Source list comes from
 *  the `useCountry()` hook (whatever the admin currently has enabled).
 *  When the list is empty (initial load / no enabled countries yet) we
 *  still render the trigger so the layout doesn't shift; clicking it
 *  shows a single disabled "Loading…" item. */
export function CountrySwitcher({ testid, country, countries, isEs, onSelect }) {
  const list = countries && countries.length > 0 ? countries : [];
  const current = list.find((c) => c.code === country);
  const label = current?.code || country || "—";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button /* design-system: ALLOW legacy-todo */
          data-testid={testid}
          className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[11px] font-bold uppercase tracking-wider text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 shrink-0"
        >
          <Globe2 className="h-3.5 w-3.5" />
          {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44 max-h-80 overflow-auto">
        <DropdownMenuLabel className="text-xs">
          {isEs ? "País" : "Country"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {list.length === 0 ? (
          <DropdownMenuItem disabled className="text-xs text-slate-400">
            {isEs ? "Cargando…" : "Loading…"}
          </DropdownMenuItem>
        ) : (
          list.map((c) => (
            <DropdownMenuItem
              key={c.code}
              data-testid={`country-option-${c.code}`}
              onClick={() => onSelect(c.code)}
              className="cursor-pointer"
            >
              <span className="flex-1 text-xs">
                <span className="font-mono mr-1.5 text-slate-500">{c.code}</span>
                {c.name || c.code}
              </span>
              {country === c.code && <Check className="h-4 w-4 text-emerald-600" />}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
